import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook leve: dado um set de contact_ids, retorna um Map<contact_id, {
 *   isCurrentAnywhere: boolean,
 *   currentCompanyIds: Set<uuid>,
 *   isExOfCompany(companyId): boolean
 * }>.
 *
 * Usado no ContactTable para badges:
 *  - "ex" quando o contato tem employment com is_current=false em uma empresa
 *    e o contato esta sendo listado sob ESSA empresa.
 *
 * Frente C resumo_enrich_employments 2026-04-26.
 */
export const useContactEmploymentStatus = (contactIds) => {
  const [statusMap, setStatusMap] = useState(new Map());
  const [loading, setLoading] = useState(false);

  const key = (contactIds || []).slice().sort().join(',');

  useEffect(() => {
    if (!contactIds || contactIds.length === 0) {
      setStatusMap(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('contact_employments')
        .select('contact_id, company_id, is_current, start_date, end_date')
        .in('contact_id', contactIds);

      if (cancelled) return;
      if (error) {
        console.error('[useContactEmploymentStatus]:', error);
        setStatusMap(new Map());
        setLoading(false);
        return;
      }

      // Mapa: contact_id -> {
      //   currentCompanyIds: Set, exCompanyIds: Set,
      //   employmentsByCompany: Map<companyId, [{is_current, start_date, end_date}]>
      // }
      const raw = new Map();
      for (const row of (data || [])) {
        if (!raw.has(row.contact_id)) {
          raw.set(row.contact_id, {
            currentCompanyIds: new Set(),
            exCompanyIds: new Set(),
            employmentsByCompany: new Map(),
          });
        }
        const entry = raw.get(row.contact_id);
        if (row.is_current) entry.currentCompanyIds.add(row.company_id);
        else entry.exCompanyIds.add(row.company_id);
        if (!entry.employmentsByCompany.has(row.company_id)) {
          entry.employmentsByCompany.set(row.company_id, []);
        }
        entry.employmentsByCompany.get(row.company_id).push({
          is_current: row.is_current,
          start_date: row.start_date,
          end_date: row.end_date,
        });
      }

      // Wrapper: adiciona helpers
      const out = new Map();
      raw.forEach((v, k) => {
        out.set(k, {
          ...v,
          isCurrentAnywhere: v.currentCompanyIds.size > 0,
          isExOfCompany: (companyId) => !v.currentCompanyIds.has(companyId) && v.exCompanyIds.has(companyId),
          getEmploymentForCompany: (companyId) => v.employmentsByCompany.get(companyId) || [],
        });
      });
      setStatusMap(out);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { statusMap, loading };
};
